<template>
  <div class="agent-message" :class="`agent-message--${roleKey}`">
    <template v-if="message.role === AgentMessageRole.User">
      <div class="agent-message__bubble">
        <div class="agent-message__meta">
          <span>{{ brandName }}</span>
          <!-- Not yet handed to the model: written while a turn was already running, and
               waiting for the next one to pick it up. -->
          <span v-if="!message.deliveredAt" class="agent-message__queued">Queued</span>
          <span :title="fullTime">{{ shortTime }}</span>
        </div>
        <div class="agent-message__text">{{ message.text }}</div>
        <router-link
          v-if="message.contextUrl && message.contextLabel"
          :to="message.contextUrl"
          class="agent-message__context"
        >
          <span class="agent-message__context-icon" v-html="linkIcon" />
          {{ message.contextLabel }}
        </router-link>
        <div v-if="message.attachments.length" class="agent-message__attachments">
          <agent-attachment v-for="attachment in message.attachments" :key="attachment.id" :attachment="attachment" />
        </div>
      </div>
    </template>

    <agent-markdown v-else-if="message.role === AgentMessageRole.Assistant" :source="message.text ?? ''" />

    <agent-tool-row v-else-if="message.role === AgentMessageRole.Tool" :message="message" />

    <div
      v-else
      class="agent-message__note"
      :class="{ 'agent-message__note--error': message.kind === AgentMessageKind.Error }"
    >
      <div>{{ message.text }}</div>
      <div v-if="message.attachments.length" class="agent-message__attachments">
        <agent-attachment v-for="attachment in message.attachments" :key="attachment.id" :attachment="attachment" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { format } from 'date-fns';
import { AgentMessageKind, AgentMessageRole, type AgentMessageDto } from '@/ApiClient';
import { parseServerUtcDate } from '@lib/formatters';
import { useUser } from '@/store/user';
import AgentAttachment from './agent-attachment.vue';
import AgentMarkdown from './agent-markdown.vue';
import AgentToolRow from './agent-tool-row.vue';
import linkIcon from '@icons/other2/link-chain.svg';

const { message } = defineProps<{ message: AgentMessageDto }>();

const roleKey = computed(() => ['user', 'assistant', 'tool', 'system'][message.role] ?? 'system');

const brandName = computed(() => {
  const companies = useUser().companies as { brandID?: number; name?: string }[];
  return companies.find((c) => c.brandID === message.brandId)?.name ?? '';
});

const created = computed(() => (message.createdAt ? parseServerUtcDate(message.createdAt) : null));
const shortTime = computed(() => (created.value ? format(created.value, 'HH:mm') : ''));
const fullTime = computed(() => (created.value ? format(created.value, 'd MMM yyyy HH:mm') : ''));
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-message {
  display: flex;

  &--user {
    justify-content: flex-end;
  }

  &--assistant,
  &--tool,
  &--system {
    justify-content: flex-start;
  }

  &--assistant > * {
    max-width: 100%;
  }

  &--tool > * {
    width: 100%;
  }

  &__bubble {
    max-width: 78%;
    padding: 1rem 1.4rem;
    border-radius: $br-heavy $br-heavy $br-light $br-heavy;
    background: $c-accent-tint;
    color: $c-ink;
  }

  &__queued {
    padding: 0.1rem 0.6rem;
    border-radius: $br-pill;
    background: rgba($c-ink, 0.08);
    font-weight: 600;
  }

  &__context {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    margin-top: 0.8rem;
    font-size: 1.2rem;
    color: $c-on-accent-soft;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  &__context-icon {
    display: flex;
    flex: none;

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: currentColor;
    }
  }

  &__meta {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.4rem;
    font-size: 1.1rem;
    color: $c-on-accent-soft;
  }

  &__text {
    font-size: 1.4rem;
    line-height: 2.1rem;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  &__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 0.8rem;
  }

  &__note {
    width: 100%;
    padding: 0.8rem 1.2rem;
    border-radius: $br-medium-heavy;
    background: $c-surface-muted;
    font-size: 1.3rem;
    color: $c-ink-soft;

    &--error {
      background: rgba($c-red, 0.08);
      color: $c-error;
    }
  }
}
</style>
